import React, { useState, useEffect, useRef } from 'react';
import { Send, Users, Sparkles, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { usePodFixer } from '@/hooks/usePodFixer';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { podChatService } from '@/services/podChatService';

interface PodMessage {
  id: string;
  user_id?: string;
  sender_id?: string;
  pod_id: string;
  message_id: string;
  sender_type: 'user' | 'ai' | 'system' | 'community';
  sender_name: string;
  content: string;
  timestamp: string;
  created_at: string;
  isSending?: boolean;
  avatar?: string;
  role?: string;
}

interface Pod {
  id: string;
  name: string;
  member_count: number;
  created_at: string;
}

const CommunityPods: React.FC = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<PodMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [pod, setPod] = useState<Pod | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Use the pod fixer to ensure proper assignment
  usePodFixer();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load user profile and pod
  useEffect(() => {
    if (!user) return;

    const loadUserData = async () => {
      try {
        // Get user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (profile?.pod_id) {
          // Get pod info separately
          const { data: podData } = await supabase
            .from('pods')
            .select('*')
            .eq('id', profile.pod_id)
            .single();

          if (podData) {
            setPod(podData);
            setUserProfile(profile);
            // Load messages immediately after pod is set
            await loadMessages(podData.id);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        toast.error('Failed to load pod data');
      }
    };

    loadUserData();
  }, [user]);

  // Set up new message listener for local AI responses
  useEffect(() => {
    if (!pod) return;

    const handleNewMessage = () => {
      if (pod?.id) {
        loadMessages(pod.id);
      }
    };

    window.addEventListener('new-message', handleNewMessage);
    return () => {
      window.removeEventListener('new-message', handleNewMessage);
    };
  }, [pod]);

  const loadMessages = async (podId: string) => {
    try {
      setIsLoading(true);
      console.log('Loading messages for pod:', podId);
      
      // Load messages from the local service
      const loadedMessages = await podChatService.loadMessages(user?.id || 'system', podId);
      console.log('Messages loaded:', loadedMessages);
      
      setMessages(loadedMessages);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
      
      // Show fallback messages even on error
      const fallbackMessages: PodMessage[] = [
        {
          id: `fallback-${Date.now()}`,
          user_id: undefined,
          pod_id: podId,
          message_id: `fallback-${Date.now()}`,
          sender_type: 'system',
          sender_name: 'Community',
          content: 'Welcome to the Product Builders community! Start sharing your journey 🎉',
          timestamp: new Date().toISOString(),
          created_at: new Date().toISOString()
        }
      ];
      setMessages(fallbackMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !pod || !user || isSending) return;

    try {
      setIsSending(true);
      console.log('Sending message:', newMessage);
      
      // Save message using the local service which triggers AI responses
      await podChatService.saveUserMessage(user.id, pod.id, newMessage.trim());
      
      setNewMessage('');
      console.log('Message sent successfully');
      toast.success('Message sent! 🚀');
      
      // Reload messages to get the latest including AI responses
      setTimeout(() => {
        loadMessages(pod.id);
      }, 500);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setNewMessage(newMessage); // Restore message on error
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getMessageBubbleStyle = (message: PodMessage) => {
    const isUser = message.user_id === user?.id;
    const isAI = message.sender_type === 'ai';

    if (isUser) {
      return 'bg-startup-teal text-white ml-auto max-w-[80%]';
    } else if (isAI) {
      return 'bg-startup-peach text-startup-navy mr-auto max-w-[80%]';
    } else {
      return 'bg-white border border-gray-200 text-startup-navy mr-auto max-w-[80%]';
    }
  };

  const getSenderInitials = (sender: string) => {
    return sender.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return `${Math.floor(diffInHours * 60)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-4">
          <MessageCircle className="h-16 w-16 text-startup-teal mx-auto" />
          <div>
            <h3 className="text-xl font-semibold text-startup-navy mb-2">Join the Community</h3>
            <p className="text-startup-gray">Sign in to connect with fellow builders</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-startup-teal mx-auto" />
          <p className="text-startup-gray">Loading your pod...</p>
        </div>
      </div>
    );
  }

  if (!pod) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-4">
          <Users className="h-16 w-16 text-startup-gray mx-auto" />
          <div>
            <h3 className="text-xl font-semibold text-startup-navy mb-2">Pod Assignment Pending</h3>
            <p className="text-startup-gray">You'll be assigned to a pod shortly!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* WhatsApp-style Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-startup-teal text-white text-sm font-semibold">
              {pod?.name ? pod.name.charAt(0).toUpperCase() : 'PB'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-medium text-gray-900">
                {pod?.name || 'Product Builders'}
              </h1>
              <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded-full">
                BETA
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {pod?.member_count} members • Chat with AI & community
            </p>
            <p className="text-xs text-gray-400 mt-1">
              🚧 Beta testing - Full userbase coming soon!
            </p>
          </div>
        </div>
      </div>

      {/* Messages Area - WhatsApp Style */}
      <div className="flex-1 bg-gray-50 overflow-hidden">
        <ScrollArea className="h-full px-4 py-2">
          <div className="space-y-2 pb-4">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-white rounded-lg shadow-sm p-6 mx-auto max-w-sm">
                  <Sparkles className="h-8 w-8 text-startup-teal mx-auto mb-3" />
                  <p className="text-gray-600 text-sm">Welcome to {pod?.name || 'Product Builders'}!</p>
                  <p className="text-xs text-gray-400 mt-1">Start sharing your entrepreneurial journey</p>
                </div>
              </div>
            ) : (
              messages.map((message) => {
                const isUser = message.user_id === user?.id;
                const isAI = message.sender_type === 'ai';
                
                return (
                  <div key={message.id} className={cn("flex gap-1", isUser && "justify-end")}>
                    <div className={cn(
                      "relative max-w-[75%] min-w-[100px] px-3 py-2 rounded-lg shadow-sm",
                      isUser
                        ? "bg-[#dcf8c6] text-gray-800 ml-auto"
                        : "bg-white text-gray-800 mr-auto"
                    )}>
                      {/* Sender name for non-user messages */}
                      {!isUser && (
                        <div className="text-xs font-medium text-startup-teal mb-1">
                          {message.sender_name}
                          {isAI && (
                            <Badge variant="secondary" className="ml-1 text-xs bg-startup-peach/20 text-startup-peach px-1 py-0">
                              AI
                            </Badge>
                          )}
                          {message.role && (
                            <span className="ml-1 text-xs text-gray-500">• {message.role}</span>
                          )}
                        </div>
                      )}
                      
                      {/* Message content */}
                      <div className="text-sm leading-relaxed break-words">
                        {message.content}
                      </div>
                      
                      {/* Timestamp */}
                      <div className={cn(
                        "text-xs mt-1 flex items-center gap-1",
                        isUser ? "justify-end text-gray-600" : "justify-end text-gray-500"
                      )}>
                        <span>{formatTimestamp(message.timestamp)}</span>
                        {isUser && <span className="text-blue-500">✓</span>}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Message Input - WhatsApp Style */}
      <div className="bg-white border-t border-gray-200 px-4 py-3">
        <div className="flex items-end gap-3">
          <div className="flex-1 bg-gray-100 rounded-full px-4 py-2 min-h-[40px] flex items-center">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message"
              className="border-0 bg-transparent p-0 text-sm placeholder-gray-500 focus-visible:ring-0 focus-visible:ring-offset-0"
              disabled={isSending}
            />
          </div>
          <Button
            onClick={sendMessage}
            disabled={!newMessage.trim() || isSending}
            className="bg-startup-teal hover:bg-startup-teal/90 text-white rounded-full w-10 h-10 p-0 flex-shrink-0"
            size="icon"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommunityPods;